import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import { getEditorStore } from "@core-ui/stores/EditorStore";
import { refreshPage } from "@core-ui/utils/initGlobalFuncs";
import FragmentUpdateService from "@ext/markdown/elements/fragment/edit/components/FragmentUpdateService";
import type { FragmentError } from "@ext/markdown/elements/fragment/edit/model/types";
import getFragmentScope from "@ext/markdown/elements/fragment/logic/getFragmentScope";
import { getEditorContext } from "@ext/markdown/elementsUtils/editorContext/EditorContext";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { whenSaved } from "../store/whenSaved";

type FragmentKey = { id: string; scope: string | undefined };

const openEditor = (): Editor | null => {
	const { editor } = getEditorStore();
	return editor && !editor.isDestroyed ? editor : null;
};

const isWaiting = (node: Node): boolean => {
	const error: FragmentError | undefined = node.attrs.error;
	return error?.reason === "scope-not-ready" && error.state === "loading";
};

/** Fragment blocks, each under the key it registered with: a catalog fragment may nest a workspace one. */
const fragmentKeys = (editor: Editor, only: (node: Node) => boolean = () => true): FragmentKey[] => {
	const keys = new Map<string, FragmentKey>();
	editor.state.doc.descendants((node) => {
		if (node.type.name !== "fragment" || !node.attrs.id || !only(node)) return;
		const scope = getFragmentScope(node.attrs.type);
		keys.set(JSON.stringify([scope ?? null, node.attrs.id]), { id: node.attrs.id, scope });
	});
	return Array.from(keys.values());
};

/** A template article renders as its title alone while its template is missing; typing gives it content of its own. */
const isBlankTemplateArticle = (editor: Editor): boolean => {
	if (!getEditorContext(editor).articleProps?.template) return false;
	let isBlank = true;
	editor.state.doc.forEach((node, _offset, index) => {
		if (index > 0 && (!node.isTextblock || node.childCount)) isBlank = false;
	});
	return isBlank;
};

/** A title typed in the meantime is saved first, so the article read back keeps it. */
const showTemplate = async (editor: Editor, apiUrlCreator: ApiUrlCreator): Promise<void> => {
	await whenSaved();
	if (!editor.isDestroyed && isBlankTemplateArticle(editor)) await ArticleUpdaterService.update(apiUrlCreator);
};

const updatesOf = (editor: Editor, apiUrlCreator: ApiUrlCreator): Promise<unknown>[] =>
	isBlankTemplateArticle(editor)
		? [showTemplate(editor, apiUrlCreator)]
		: fragmentKeys(editor).map(({ id, scope }) => FragmentUpdateService.updateContent(id, apiUrlCreator, scope));

/** An open editor only refetches what came from the tools: a page refresh would move its caret to the start. */
export const showLandedTools = async (apiUrlCreator: ApiUrlCreator): Promise<void> => {
	const editor = openEditor();
	if (!editor) {
		refreshPage();
		return;
	}
	await Promise.allSettled(updatesOf(editor, apiUrlCreator));
};

const waitsForTools = (editor: Editor): boolean => fragmentKeys(editor, isWaiting).length > 0;

export const showFailedTools = async (apiUrlCreator: ApiUrlCreator): Promise<void> => {
	const editor = openEditor();
	if (!editor) {
		refreshPage();
		return;
	}
	if (!waitsForTools(editor)) return;
	await whenSaved();
	if (!editor.isDestroyed) await ArticleUpdaterService.update(apiUrlCreator);
};

/** After a reload that deleted nothing, waiting fragments refetch; any other view re-parses on its next load. */
export const showWaitingTools = async (apiUrlCreator: ApiUrlCreator): Promise<void> => {
	const editor = openEditor();
	if (!editor) return;
	const updates = fragmentKeys(editor, isWaiting).map(({ id, scope }) =>
		FragmentUpdateService.updateContent(id, apiUrlCreator, scope),
	);
	await Promise.allSettled(updates);
};
